import { createContext, useContext } from 'react';

type PatientScopeInstances = Map<object, unknown>;

export const PatientScopeContext = createContext<PatientScopeInstances | null>(null);
PatientScopeContext.displayName = 'PatientScopeContext';

/**
 * Define state that belongs to one patient scope.
 *
 * Returns a hook that gives every component inside the same
 * `PatientScopeProvider` the same instance, built by `create` the first time
 * it is asked for. Each new scope starts from a fresh instance, so nothing
 * carries over to another patient. A callback that captured an instance keeps
 * pointing at its own scope's instance after the scope ends, so late writes
 * can't reach the next patient.
 *
 * The hook throws when called outside a `PatientScopeProvider`.
 */
export function definePatientScoped<T>(create: () => T): () => T {
  const token = {};
  return function usePatientScoped(): T {
    const instances = useContext(PatientScopeContext);
    if (instances === null) {
      throw new Error('Patient-scoped state was used outside a PatientScopeProvider.');
    }
    if (!instances.has(token)) {
      instances.set(token, create());
    }
    return instances.get(token) as T;
  };
}
